import { useTour } from '@reactour/tour';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { useModal } from '@/components/common/modal';
import { PageLoadingSpinner } from '@/components/common/page-loading-spinner';
import { TopBar } from '@/components/common/top-bar';
import { Button, ButtonVariant } from '@/components/form/button';
import { Input } from '@/components/form/input';
import { SignedInMethod, useAuth } from '@/features/auth';
import { getTourStep } from '@/features/demo/steps';
import { useRoute } from '@/hooks/use-route';
import { MeasurementOverviewLayout } from '@/layouts';
import { isResultReady } from '@/utils/is-result-ready';
import { Theme } from '@/utils/types';

import {
  ConcentrationPercentage,
  ViabilityPercentage,
  ViableConcentrationPercentage,
} from '../../single-measurement/components/percentage-cards';
import { getBatch } from '../../single-measurement/services/batches';
import { SingleMeasurmentQueryKeys } from '../../single-measurement/services/query-keys';
import { State } from '../../single-measurement/types';
import { PitchRateSettingsModal } from '../components/pitch-rate-settings-modal';
import { YeastVolumeItem } from '../components/yeast-volume-item';
import { PitchRateContext, PitchRateProvider } from '../provider/provider';
import { ActionKind } from '../provider/types';
import { getWortUnitSymbol } from '../utils/get-wort-unit-symbol';

const wortVolumeLabelMatrix = {
  [SignedInMethod.Wine]: 'Must',
  [SignedInMethod.Beer]: 'Wort',
  [SignedInMethod.Ethanol]: '',
};

const textColorMatrix = {
  [Theme.Beer]: 'text-[#008A00]',
  [Theme.Wine]: 'text-[#EF426F]',
  [Theme.Ethanol]: 'text-[#117EA6]',
};

const FWPitchRatePage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { routeToAnalysis } = useRoute();
  const { batchId = '' } = useParams();
  const { isOpen, toggle } = useModal();
  const { state, dispatch } = useContext(PitchRateContext);
  const [concentrationError, setConcentrationError] = useState<FieldError | undefined>(
    undefined,
  );
  const theme: any = useTheme();

  // color of the 'calculator settings' text
  const textColor = textColorMatrix[theme.current as Theme];

  // wort or must terms changed based on type of analysis
  const wortLabel = user ? wortVolumeLabelMatrix[user?.signedInMethod] : '';

  const { isLoading, isFetched, data } = useQuery({
    queryKey: [SingleMeasurmentQueryKeys.GetABatch, batchId],
    queryFn: () => getBatch(batchId),
  });

  // demo
  const {
    isOpen: isTourOpen,
    setIsOpen: setIsTourOpen,
    setCurrentStep,
    currentStep,
    steps,
  } = useTour();
  const [wasTourOpen, setWasTourOpen] = useState<Boolean>(false);

  // a trick to wait until the data is loaded
  useEffect(() => {
    setWasTourOpen(isTourOpen); // remember the tour state
    setIsTourOpen(false); // temp turn off the tour, once data is loaded then we can set it open
  }, []);

  // set viab and conz in initial load
  useEffect(() => {
    if (data?.resultStat.viab)
      dispatch({ type: ActionKind.SetViability, payload: data?.resultStat.viab });
    if (data?.resultStat.conz)
      dispatch({ type: ActionKind.SetConcentration, payload: data?.resultStat.conz });

    // target Concentration field is pre-filled with a 2
    // to give users an indication what range to use and 2 is the most common
    if (user?.signedInMethod === SignedInMethod.Wine)
      dispatch({ type: ActionKind.SetTargetConcentration, payload: 2 });

    // if tour was open, then we open the tour again
    if (wasTourOpen) {
      setIsTourOpen(true);
      setCurrentStep(getTourStep(steps, 'gOTK8sl'));
    }
  }, [data]);

  useEffect(() => {
    // auto fill in demo mode
    // we have delayed the input for better UX
    if (currentStep === getTourStep(steps, 'BA1Qa3z'))
      setTimeout(function () {
        dispatch({ type: ActionKind.SetWort, payload: 20 });
      }, 250);

    if (currentStep === getTourStep(steps, 'zofpyzw'))
      setTimeout(function () {
        dispatch({ type: ActionKind.SetTargetConcentration, payload: 2 });
      }, 250);
  }, [currentStep]);

  // target concentration validation
  useEffect(() => {
    const { targetConcentration, concentration } = state;
    if (targetConcentration && concentration && targetConcentration > concentration) {
      setConcentrationError({ type: 'max', message: t('pitcRate.concentrationError') });
    } else {
      setConcentrationError(undefined);
    }
  }, [state.targetConcentration]);

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type:
      | ActionKind.SetWort
      | ActionKind.Calculate
      | ActionKind.SetGravity
      | ActionKind.SetPitchRate
      | ActionKind.SetTargetConcentration,
  ) => {
    // @ts-expect-error
    dispatch({ type, payload: e.target.value });
  };

  const wortUnitSymbol = useMemo(
    () => getWortUnitSymbol(state.wortUnit),
    [state.wortUnit],
  );

  const handleOnSettingsChange = () => {
    dispatch({ type: ActionKind.Reset });
  };

  return (
    <>
      <PitchRateSettingsModal
        key={Date.now()}
        isOpen={isOpen}
        toggle={toggle}
        onSave={handleOnSettingsChange}
      />
      <MeasurementOverviewLayout>
        <MeasurementOverviewLayout.Title title="Pitch Calculator" />

        <TopBar onBack={() => routeToAnalysis(batchId)}>
          <div id="step-pitch-settings-btn">
            <Button
              onClick={toggle}
              disabled={!isFetched}
              variant={ButtonVariant.Link}
              classnames="flex items-center gap-2"
            >
              <span className={`text-sm font-semibold ${textColor}`}>
                Calculator Settings
              </span>
            </Button>
          </div>
        </TopBar>

        <MeasurementOverviewLayout.Body>
          {isLoading ? <PageLoadingSpinner /> : null}

          {isFetched &&
          [State.UploadCompleted, State.InProgress].includes(data!.state) ? (
            <PageLoadingSpinner>Analysis is still in progress..</PageLoadingSpinner>
          ) : null}

          {isFetched && data && isResultReady(data?.state) ? (
            <div className="flex w-full mt-6 gap-16">
              <div className="flex flex-col gap-8 w-8/12">
                <div id="step-wort-volume" className="flex items-center">
                  <label className="text-lg text-gray-700 w-full" htmlFor="wort">
                    {wortLabel} Volume [{wortUnitSymbol}]
                  </label>
                  <Input
                    name="wort"
                    lang="de-DE"
                    type="number"
                    value={state.wort ?? ''}
                    className="text-center font-bold pl-6 w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetWort)}
                  />
                </div>

                <div id="step-target-concentration" className="flex items-center">
                  <label className="text-lg text-gray-700 w-full" htmlFor="wort">
                    Target Concentration [million cells/ml]
                  </label>
                  <Input
                    lang="de-DE"
                    type="number"
                    name="targetConcentration"
                    value={state.targetConcentration ?? ''}
                    className="text-center font-bold pl-6 w-60"
                    onChange={(e) => onInputChange(e, ActionKind.SetTargetConcentration)}
                    errors={{ targetConcentration: concentrationError }}
                  />
                </div>

                <div className="flex gap-6 justify-end">
                  <Button
                    id="step-pitch-rate-calc-btn"
                    onClick={() => dispatch({ type: ActionKind.Calculate })}
                  >
                    Calculate
                  </Button>
                </div>

                <div>
                  <div className="flex text-gray-600 font-bold py-2 border-b-2">
                    <span className="flex-1">Volume of Yeast</span>
                    <span className="flex-1">Weight of Yeast</span>
                  </div>
                  <div className="flex mt-4 text-gray-700 text-lg">
                    <div className="flex flex-col flex-1 gap-4">
                      <YeastVolumeItem unit="ml" volume={state.yeastVolume?.MILILITER} />
                      <YeastVolumeItem unit="l" volume={state.yeastVolume?.LITER} />
                      <YeastVolumeItem
                        unit="hl"
                        volume={state.yeastVolume?.HECTORLITER}
                      />
                      <YeastVolumeItem unit="gal" volume={state.yeastVolume?.GALLON} />
                      <YeastVolumeItem unit="bbl" volume={state.yeastVolume?.BARRELS} />
                    </div>
                    <div className="flex flex-col flex-1 gap-4">
                      <YeastVolumeItem unit="g" volume={state.yeastVolume?.GRAM} />
                      <YeastVolumeItem unit="kg" volume={state.yeastVolume?.KILOGRAM} />
                      <YeastVolumeItem unit="lb" volume={state.yeastVolume?.POUND} />
                      <YeastVolumeItem unit="oz" volume={state.yeastVolume?.OUNCE} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-16 w-4/12">
                {data.resultStat.viab ? (
                  <>
                    <ViabilityPercentage viability={data.resultStat.viab} />
                    <ViableConcentrationPercentage
                      concentration={data.resultStat.conz}
                      viability={data.resultStat.viab}
                    />
                  </>
                ) : null}
                <ConcentrationPercentage concentration={data.resultStat.conz} />
              </div>
            </div>
          ) : null}
        </MeasurementOverviewLayout.Body>
      </MeasurementOverviewLayout>
    </>
  );
};

export const FWPitchRateCalculator = () => (
  <PitchRateProvider>
    <FWPitchRatePage />
  </PitchRateProvider>
);
